/**
 * Event Service - Funkcje do zarządzania wydarzeniami
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type {
  Event,
  EventSpeaker,
  EventOrganizer,
  EventAttendee,
  EventFile,
  EventWithDetails,
  EventStatus,
  RegistrationStatus,
  PaymentStatus,
  Author,
} from '../types/database.types';

// ========================================
// TYPES
// ========================================

export interface CreateEventData {
  title: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  timezone?: string;
  is_virtual?: boolean;
  location_name?: string;
  location_address?: string;
  location_city?: string;
  location_country?: string;
  location_coordinates?: { lat: number; lng: number };
  meeting_link?: string;
  meeting_password?: string;
  organizer_website_link?: string;
  is_free?: boolean;
  price_amount?: number;
  price_currency?: string;
  price_description?: string;
  max_attendees?: number;
  category?: string;
  tags?: string[];
  cover_image_url?: string;
  status?: EventStatus;
  speakers?: Array<{
    author_id: string;
    role?: string;
    order?: number;
  }>;
  organizers?: Array<{
    name: string;
    email?: string;
    phone?: string;
    bio?: string;
    role?: string;
    order?: number;
    user_id?: string;
  }>;
}

export interface UpdateEventData extends Partial<CreateEventData> {
  id: string;
}

// ========================================
// EVENTS CRUD
// ========================================

/**
 * Tworzy nowe wydarzenie
 */
export async function createEvent(
  eventData: CreateEventData,
  userId: string
): Promise<{ success: boolean; data?: Event; error?: string }> {
  try {
    const { speakers, organizers, ...eventFields } = eventData;

    // Insert event
    const { data: event, error: eventError } = await supabase
      .from('events')
      .insert({
        ...eventFields,
        created_by: userId,
        timezone: eventData.timezone || 'Europe/Warsaw',
        is_virtual: eventData.is_virtual ?? false,
        is_free: eventData.is_free ?? true,
        price_currency: eventData.price_currency || 'PLN',
        location_country: eventData.location_country || 'Poland',
        status: eventData.status || 'draft',
      })
      .select()
      .single();

    if (eventError) throw eventError;
    if (!event) throw new Error('Failed to create event');

    // Insert speakers
    if (speakers && speakers.length > 0) {
      const speakersData = speakers.map((speaker, index) => ({
        event_id: event.id,
        author_id: speaker.author_id,
        speaker_order: speaker.order ?? index,
        role: speaker.role || 'prelegent',
      }));

      const { error: speakersError } = await supabase
        .from('event_speakers')
        .insert(speakersData);

      if (speakersError) {
        console.error('Error inserting speakers:', speakersError);
      }
    }

    // Insert organizers
    if (organizers && organizers.length > 0) {
      const organizersData = organizers.map((organizer, index) => ({
        event_id: event.id,
        user_id: organizer.user_id || null,
        organizer_name: organizer.name,
        organizer_email: organizer.email || null,
        organizer_phone: organizer.phone || null,
        organizer_bio: organizer.bio || null,
        organizer_role: organizer.role || 'main',
        organizer_order: organizer.order ?? index,
      }));

      const { error: organizersError } = await supabase
        .from('event_organizers')
        .insert(organizersData);

      if (organizersError) {
        console.error('Error creating organizers:', organizersError);
        // Event is created, but organizers failed - not critical
      }
    }

    return { success: true, data: event };
  } catch (error: any) {
    console.error('Error creating event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje wydarzenie
 */
export async function updateEvent(
  eventData: UpdateEventData
): Promise<{ success: boolean; data?: Event; error?: string }> {
  try {
    const { id, speakers, organizers, ...updateFields } = eventData;

    // Update event
    const { data: event, error: eventError } = await supabase
      .from('events')
      .update(updateFields)
      .eq('id', id)
      .select()
      .single();

    if (eventError) throw eventError;
    if (!event) throw new Error('Event not found');

    // Update speakers if provided
    if (speakers !== undefined) {
      // Delete existing speakers
      await supabase.from('event_speakers').delete().eq('event_id', id);

      // Insert new speakers
      if (speakers.length > 0) {
        const speakersData = speakers.map((speaker, index) => ({
          event_id: id,
          author_id: speaker.author_id,
          speaker_order: speaker.order ?? index,
          role: speaker.role || 'prelegent',
        }));

        const { error: speakersError } = await supabase
          .from('event_speakers')
          .insert(speakersData);

        if (speakersError) {
          console.error('Error updating speakers:', speakersError);
        }
      }
    }

    // Update organizers if provided
    if (organizers) {
      // Delete existing organizers
      await supabase.from('event_organizers').delete().eq('event_id', id);

      // Insert new organizers
      if (organizers.length > 0) {
        const organizersData = organizers.map((organizer, index) => ({
          event_id: id,
          user_id: organizer.user_id || null,
          organizer_name: organizer.name,
          organizer_email: organizer.email || null,
          organizer_phone: organizer.phone || null,
          organizer_bio: organizer.bio || null,
          organizer_role: organizer.role || 'main',
          organizer_order: organizer.order ?? index,
        }));

        const { error: organizersError } = await supabase
          .from('event_organizers')
          .insert(organizersData);

        if (organizersError) {
          console.error('Error updating organizers:', organizersError);
        }
      }
    }

    return { success: true, data: event };
  } catch (error: any) {
    console.error('Error updating event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa wydarzenie
 */
export async function deleteEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from('events').delete().eq('id', eventId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wydarzenie po ID
 */
export async function getEventById(
  eventId: string
): Promise<{ success: boolean; data?: EventWithDetails; error?: string }> {
  try {
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select(
        `
        *,
        event_speakers (*, author:authors(*)),
        event_organizers (*),
        event_files (*)
      `
      )
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;
    if (!event) throw new Error('Event not found');

    // Transform to EventWithDetails
    const result: EventWithDetails = {
      ...event,
      speakers: event.event_speakers || [],
      organizers: event.event_organizers || [],
      files: event.event_files || [],
    };

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wydarzenie po slug
 */
export async function getEventBySlug(
  slug: string
): Promise<{ success: boolean; data?: EventWithDetails; error?: string }> {
  try {
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select(
        `
        *,
        event_speakers (*, author:authors(*)),
        event_organizers (*),
        event_files (*)
      `
      )
      .eq('slug', slug)
      .single();

    if (eventError) throw eventError;
    if (!event) throw new Error('Event not found');

    // Transform to EventWithDetails
    const result: EventWithDetails = {
      ...event,
      speakers: event.event_speakers || [],
      organizers: event.event_organizers || [],
      files: event.event_files || [],
    };

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching event by slug:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wszystkie wydarzenia (z opcjonalnymi filtrami)
 */
export async function getAllEvents(options?: {
  includeUnpublished?: boolean;
  category?: string;
  tag?: string;
  search?: string;
  upcoming?: boolean;
  past?: boolean;
  limit?: number;
}): Promise<{ success: boolean; data?: Event[]; error?: string }> {
  try {
    let query = supabase
      .from('events')
      .select(
        `
        *,
        event_organizers (organizer_name)
      `
      )
      .order('start_date', { ascending: !options?.past });

    // Filter by status
    if (!options?.includeUnpublished) {
      query = query.eq('status', 'published');
    }

    // Filter upcoming or past
    if (options?.upcoming) {
      // Show events that either have no start_date OR start_date is in the future
      query = query.or(`start_date.is.null,start_date.gte.${new Date().toISOString()}`);
    } else if (options?.past) {
      // Show only events with end_date in the past
      query = query.not('end_date', 'is', null).lt('end_date', new Date().toISOString());
    }

    // Filter by category
    if (options?.category) {
      query = query.eq('category', options.category);
    }

    // Filter by tag
    if (options?.tag) {
      query = query.contains('tags', [options.tag]);
    }

    // Search
    if (options?.search) {
      query = query.or(
        `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    // Limit
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching events:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera nadchodzące wydarzenia
 */
export async function getUpcomingEvents(
  limit: number = 10
): Promise<{ success: boolean; data?: Event[]; error?: string }> {
  return getAllEvents({ upcoming: true, limit });
}

/**
 * Pobiera przeszłe wydarzenia
 */
export async function getPastEvents(
  limit: number = 10
): Promise<{ success: boolean; data?: Event[]; error?: string }> {
  return getAllEvents({ past: true, limit });
}

/**
 * Publikuje wydarzenie (zmienia status na 'published')
 */
export async function publishEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('events')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
      })
      .eq('id', eventId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error publishing event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Anuluje wydarzenie
 */
export async function cancelEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('events')
      .update({ status: 'cancelled' })
      .eq('id', eventId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Oznacza wydarzenie jako zakończone
 */
export async function completeEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('events')
      .update({ status: 'completed' })
      .eq('id', eventId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error completing event:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// EVENT REGISTRATION / ATTENDEES
// ========================================

/**
 * Rejestruje użytkownika na wydarzenie
 */
export async function registerForEvent(
  eventId: string,
  userId: string,
  options?: {
    notes?: string;
    payment_status?: PaymentStatus;
  }
): Promise<{ success: boolean; data?: EventAttendee; error?: string }> {
  try {
    // Check if event is full
    const { data: event } = await supabase
      .from('events')
      .select('max_attendees, current_attendees')
      .eq('id', eventId)
      .single();

    if (
      event?.max_attendees &&
      event.current_attendees >= event.max_attendees
    ) {
      return { success: false, error: 'Event is full' };
    }

    // Check if already registered
    const { data: existing } = await supabase
      .from('event_attendees')
      .select('id, registration_status')
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .single();

    if (existing) {
      // Jeśli rejestracja była anulowana, pozwól na ponowną rejestrację
      if (existing.registration_status === 'cancelled') {
        const { data, error } = await supabase
          .from('event_attendees')
          .update({
            registration_status: 'registered',
            notes: options?.notes || null,
            payment_status: options?.payment_status || 'free',
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return { success: true, data };
      }

      // W innych przypadkach użytkownik jest już zarejestrowany
      return { success: false, error: 'Already registered for this event' };
    }

    // Register (nowy użytkownik)
    const { data, error } = await supabase
      .from('event_attendees')
      .insert({
        event_id: eventId,
        user_id: userId,
        registration_status: 'registered',
        notes: options?.notes || null,
        payment_status: options?.payment_status || 'free',
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error registering for event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Anuluje rejestrację użytkownika
 */
export async function cancelRegistration(
  eventId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Update status to cancelled instead of deleting
    const { error } = await supabase
      .from('event_attendees')
      .update({ registration_status: 'cancelled' })
      .eq('event_id', eventId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling registration:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa rejestrację użytkownika (hard delete)
 */
export async function deleteRegistration(
  eventId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('event_attendees')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting registration:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje status rejestracji
 */
export async function updateRegistrationStatus(
  attendeeId: string,
  status: RegistrationStatus
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('event_attendees')
      .update({ registration_status: status })
      .eq('id', attendeeId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error updating registration status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje status płatności
 */
export async function updatePaymentStatus(
  attendeeId: string,
  status: PaymentStatus
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('event_attendees')
      .update({ payment_status: status })
      .eq('id', attendeeId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error updating payment status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera uczestników wydarzenia
 */
export async function getEventAttendees(
  eventId: string
): Promise<{ success: boolean; data?: EventAttendee[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('event_attendees')
      .select('*')
      .eq('event_id', eventId)
      .order('registration_date', { ascending: false });

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching event attendees:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sprawdza czy użytkownik jest zarejestrowany na wydarzenie
 */
export async function isUserRegistered(
  eventId: string,
  userId: string
): Promise<{ success: boolean; data?: boolean; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('event_attendees')
      .select('id, registration_status')
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found

    const isRegistered = data && data.registration_status !== 'cancelled';

    return { success: true, data: !!isRegistered };
  } catch (error: any) {
    console.error('Error checking registration:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// EVENT FILES MANAGEMENT
// ========================================

/**
 * Pobiera pliki wydarzenia
 */
export async function getEventFiles(
  eventId: string
): Promise<{ success: boolean; data?: EventFile[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('event_files')
      .select('*')
      .eq('event_id', eventId)
      .order('uploaded_at', { ascending: false });

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching event files:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Dodaje plik do wydarzenia
 */
export async function addEventFile(
  eventId: string,
  fileData: {
    file_name: string;
    file_path: string;
    file_type: string;
    file_size: number;
    mime_type: string;
    description?: string;
    is_public?: boolean;
  },
  userId?: string
): Promise<{ success: boolean; data?: EventFile; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('event_files')
      .insert({
        event_id: eventId,
        ...fileData,
        is_public: fileData.is_public ?? false,
        uploaded_by: userId || null,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error adding event file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa plik wydarzenia
 */
export async function deleteEventFile(fileId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Pobierz informacje o pliku
    const { data: file, error: fetchError } = await supabase
      .from('event_files')
      .select('file_path')
      .eq('id', fileId)
      .single();

    if (fetchError) throw fetchError;

    // Usuń z bazy danych
    const { error: deleteError } = await supabase
      .from('event_files')
      .delete()
      .eq('id', fileId);

    if (deleteError) throw deleteError;

    // Usuń z Storage (jeśli plik jest w Supabase Storage)
    if (file?.file_path && file.file_path.includes('event-files')) {
      try {
        const urlParts = new URL(file.file_path);
        const pathParts = urlParts.pathname.split('/');
        const storagePathIndex = pathParts.indexOf('event-files') + 1;
        const storagePath = pathParts.slice(storagePathIndex).join('/');

        await supabase.storage.from('event-files').remove([storagePath]);
      } catch (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Nie przerywa operacji - plik w bazie jest już usunięty
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting event file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Upload pliku do Supabase Storage
 */
export async function uploadEventFile(
  file: File,
  eventId?: string
): Promise<{ success: boolean; data?: { path: string; url: string }; error?: string }> {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomStr}.${ext}`;
    const filePath = eventId ? `events/${eventId}/${fileName}` : `temp/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('event-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('event-files')
      .getPublicUrl(filePath);

    return {
      success: true,
      data: {
        path: data.path,
        url: urlData.publicUrl,
      },
    };
  } catch (error: any) {
    console.error('Error uploading file:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// STATISTICS
// ========================================

/**
 * Pobiera statystyki wydarzeń
 */
export async function getEventsStats(includeUnpublished: boolean = false): Promise<{
  success: boolean;
  data?: {
    totalEvents: number;
    publishedEvents: number;
    upcomingEvents: number;
    pastEvents: number;
    categories: string[];
    tags: string[];
    totalAttendees: number;
  };
  error?: string;
}> {
  try {
    let query = supabase.from('events').select(
      `
        id,
        status,
        category,
        tags,
        start_date,
        end_date,
        current_attendees
      `
    );

    // Filtruj tylko opublikowane na stronie publicznej
    if (!includeUnpublished) {
      query = query.eq('status', 'published');
    }

    const { data: events, error } = await query;

    if (error) throw error;

    const now = new Date().toISOString();
    const publishedEvents = events?.filter((e) => e.status === 'published').length ?? 0;
    const upcomingEvents =
      events?.filter((e) => e.status === 'published' && e.start_date && e.start_date > now).length ?? 0;
    const pastEvents = events?.filter((e) => e.status === 'published' && e.end_date && e.end_date < now).length ?? 0;

    const categories = [...new Set(events?.map((e) => e.category).filter(Boolean))] as string[];

    const tags = [...new Set(events?.flatMap((e) => e.tags || []))] as string[];

    const totalAttendees = events?.reduce((sum, e) => sum + (e.current_attendees || 0), 0) ?? 0;

    return {
      success: true,
      data: {
        totalEvents: events?.length ?? 0,
        publishedEvents,
        upcomingEvents,
        pastEvents,
        categories,
        tags,
        totalAttendees,
      },
    };
  } catch (error: any) {
    console.error('Error fetching events stats:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wydarzenia użytkownika (na które jest zarejestrowany)
 */
export async function getUserEvents(
  userId: string
): Promise<{ success: boolean; data?: Event[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('event_attendees')
      .select(
        `
        *,
        events (*)
      `
      )
      .eq('user_id', userId)
      .order('registration_date', { ascending: false });

    if (error) throw error;

    const events = data?.map((attendee: any) => attendee.events).filter(Boolean) ?? [];

    return { success: true, data: events };
  } catch (error: any) {
    console.error('Error fetching user events:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// AUTHORS (for speakers)
// ========================================

/**
 * Pobiera wszystkich autorów (do wyboru jako prelegenci)
 */
export async function getAllAuthors(): Promise<{
  success: boolean;
  data?: Author[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('authors')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error('Error fetching authors:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nowego autora
 */
export async function createAuthor(
  name: string,
  bio?: string,
  website_url?: string
): Promise<{
  success: boolean;
  data?: Author;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('authors')
      .insert({
        name: name.trim(),
        bio: bio?.trim() || null,
        website_url: website_url?.trim() || null,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating author:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// EVENT SPEAKERS
// ========================================

/**
 * Pobiera prelegentów wydarzenia z danymi autorów
 */
export async function getEventSpeakers(
  eventId: string
): Promise<{
  success: boolean;
  data?: EventSpeaker[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('event_speakers')
      .select(`
        *,
        author:authors(*)
      `)
      .eq('event_id', eventId)
      .order('speaker_order', { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error('Error fetching event speakers:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Dodaje prelegenta do wydarzenia
 */
export async function addEventSpeaker(
  eventId: string,
  authorId: string,
  role?: string,
  order?: number
): Promise<{
  success: boolean;
  data?: EventSpeaker;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('event_speakers')
      .insert({
        event_id: eventId,
        author_id: authorId,
        role: role || 'prelegent',
        speaker_order: order ?? 0,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error adding event speaker:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa prelegenta z wydarzenia
 */
export async function removeEventSpeaker(
  speakerId: string
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('event_speakers')
      .delete()
      .eq('id', speakerId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error removing event speaker:', error);
    return { success: false, error: error.message };
  }
}

